import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export const SpaqCoreSection: React.FC = () => {
  return (
    <section
      className="spaq-core py-20 sm:py-24 lg:py-28 bg-[#0B1B26] text-white overflow-hidden"
      aria-labelledby="spaq-core-heading"
    >
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left Column: Copy & Text (~38%) */}
          <div className="lg:col-span-5 flex flex-col justify-center space-y-6">
            <span className="text-xs font-bold tracking-[0.2em] text-[#38B5DE] uppercase">
              CORE TECHNOLOGY
            </span>

            <div className="space-y-3">
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
                SPAQ CORE
              </h2>
              <h3
                id="spaq-core-heading"
                className="text-2xl sm:text-[30px] font-bold text-white leading-snug"
              >
                設備をつなぎ、<br />
                電力を最適化する。
              </h3>
            </div>

            <p className="text-base text-[#94A3B8] leading-relaxed">
              太陽光・空調・蓄電池を、ひとつの知性で。
            </p>

            <div className="pt-2 hidden lg:block">
              <Link
                to="/products/spaq-core"
                className="inline-flex items-center gap-3 px-6 py-3.5 border border-white/30 hover:border-white text-white font-medium text-sm rounded transition-colors group"
              >
                <span>技術を見る</span>
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </Link>
            </div>
          </div>

          {/* Right Column: High-Precision Vector Dashboard Mock (~62%) */}
          <div className="lg:col-span-7">
            <div className="bg-[#0E2230]/90 border border-white/10 rounded-xl p-5 sm:p-7 shadow-2xl backdrop-blur-sm relative">
              {/* Header / Legends */}
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 mb-4 border-b border-white/10 text-xs text-slate-300">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-white tracking-wide">
                    電力デマンド推移
                  </span>
                  <span className="text-[11px] font-mono text-slate-400">
                    (kW)
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-3.5 sm:gap-4 text-[11px]">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-3 h-0.5 bg-[#38B5DE] rounded-full inline-block" />
                    <span>電力デマンド</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-3 h-0.5 bg-[#EAB308] rounded-full inline-block" />
                    <span>太陽光</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-3 h-0.5 bg-[#3B82F6] rounded-full inline-block" />
                    <span>空調</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-3 h-0.5 bg-[#10B981] rounded-full inline-block" />
                    <span>蓄電池</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-slate-400">
                    <span className="w-3 border-t border-dashed border-white inline-block" />
                    <span>目標デマンド</span>
                  </span>
                </div>
              </div>

              {/* Main Waveform SVG Graph */}
              <div className="relative w-full aspect-[16/9] min-h-[220px]">
                <svg
                  viewBox="0 0 600 300"
                  className="w-full h-full overflow-visible select-none"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient id="pvFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EAB308" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#EAB308" stopOpacity="0.0" />
                    </linearGradient>
                    <linearGradient id="demandFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38B5DE" stopOpacity="0.2" />
                      <stop offset="100%" stopColor="#38B5DE" stopOpacity="0.0" />
                    </linearGradient>
                  </defs>

                  {/* Horizontal Grid lines (0, 100, 200, 300, 400 kW) */}
                  <g className="text-white/20 stroke-current" strokeWidth="1">
                    <line x1="55" y1="40" x2="570" y2="40" strokeDasharray="3 3" />
                    <line x1="55" y1="95" x2="570" y2="95" strokeDasharray="3 3" />
                    <line x1="55" y1="150" x2="570" y2="150" strokeDasharray="3 3" />
                    <line x1="55" y1="205" x2="570" y2="205" strokeDasharray="3 3" />
                    <line x1="55" y1="260" x2="570" y2="260" strokeDasharray="none" className="text-white/40" />
                  </g>

                  {/* Y Axis Labels */}
                  <g className="fill-slate-400 text-[10px] font-mono select-none" textAnchor="end">
                    <text x="48" y="44">400</text>
                    <text x="48" y="99">300</text>
                    <text x="48" y="154">200</text>
                    <text x="48" y="209">100</text>
                    <text x="48" y="264">0</text>
                  </g>

                  {/* X Axis Labels */}
                  <g className="fill-slate-400 text-[10px] font-mono select-none" textAnchor="middle">
                    <text x="55" y="282">0:00</text>
                    <text x="184" y="282">6:00</text>
                    <text x="313" y="282">12:00</text>
                    <text x="442" y="282">18:00</text>
                    <text x="570" y="282">24:00</text>
                  </g>

                  {/* Target Demand (Horizontal dashed line at 300kW -> y=95) */}
                  <line
                    x1="55"
                    y1="95"
                    x2="570"
                    y2="95"
                    stroke="#FFFFFF"
                    strokeWidth="1.5"
                    strokeDasharray="6 4"
                    opacity="0.8"
                  />

                  {/* Solar Curve (PV Generation: 6:00 to 18:00 peak ~260kW) */}
                  <path
                    d="M 184 260 Q 230 250, 260 170 Q 313 75, 365 160 Q 405 250, 442 260 Z"
                    fill="url(#pvFill)"
                  />
                  <path
                    d="M 55 260 L 184 260 Q 230 250, 260 170 Q 313 75, 365 160 Q 405 250, 442 260 L 570 260"
                    fill="none"
                    stroke="#EAB308"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />

                  {/* Air Conditioning Curve (Blue) */}
                  <path
                    d="M 55 240 Q 140 235, 200 200 Q 280 180, 350 195 Q 430 210, 500 230 L 570 245"
                    fill="none"
                    stroke="#3B82F6"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    opacity="0.9"
                  />

                  {/* Battery Curve (Green - Charge in morning, Discharge in afternoon peak) */}
                  <path
                    d="M 55 260 Q 120 270, 200 255 Q 280 245, 330 240 Q 410 185, 460 210 Q 510 255, 570 260"
                    fill="none"
                    stroke="#10B981"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />

                  {/* Controlled Facility Demand Curve (Cyan - Smoothly kept below 300kW) */}
                  <path
                    d="M 55 200 Q 120 195, 184 175 Q 240 160, 280 110 Q 320 100, 370 120 Q 420 105, 470 115 Q 520 160, 570 195"
                    fill="none"
                    stroke="#38B5DE"
                    strokeWidth="2.8"
                    strokeLinecap="round"
                  />
                </svg>
              </div>

              {/* Bottom: Battery State of Charge (SoC) Indicator */}
              <div className="mt-4 pt-3 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                <span className="text-slate-300 font-medium">蓄電池充放電率</span>
                <div className="flex items-center gap-3 flex-1 sm:max-w-md">
                  <span className="text-[10px] font-mono text-slate-400">0%</span>
                  <div className="flex-1 flex gap-1 h-3 items-center">
                    {[
                      true, true, true, true, true, true, true, true, true, true, true,
                      false, false, false, false, false,
                    ].map((filled, i) => (
                      <span
                        key={i}
                        className={`h-full flex-1 rounded-sm ${
                          filled
                            ? 'bg-gradient-to-r from-[#38B5DE] to-[#10B981]'
                            : 'bg-white/10'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-[10px] font-mono text-slate-400">100%</span>
                </div>
              </div>

              {/* Caption */}
              <div className="text-right mt-2 text-[10px] text-white/40 font-normal">
                画面イメージ
              </div>
            </div>

            {/* Mobile CTA (Below dashboard) */}
            <div className="mt-6 lg:hidden">
              <Link
                to="/products/spaq-core"
                className="w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 border border-white/30 hover:border-white text-white font-medium text-sm rounded transition-colors"
              >
                <span>技術を見る</span>
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
